import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import COLLEGE_MAP from "../../constants/colleges";
import {
  FaCheckCircle,
  FaTimesCircle,
  FaPhoneAlt,
  FaEnvelope,
  FaInstagram,
  FaGraduationCap,
  FaBirthdayCake,
  FaTrophy,
  FaLightbulb,
  FaDumbbell,
  FaExclamationTriangle,
  FaPalette,
  FaQuoteLeft,
} from "react-icons/fa";
import { AlertCircle, RefreshCw } from "lucide-react";
import "../Css/Profile.css";
import API_BASE_URL, { API_IMAGE_BASE_URL } from "../../api/config";

const ProfileCard = () => {
  const [activeTab, setActiveTab] = useState("Timeline");
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const navigate = useNavigate();

  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };

  const handleLogout = () => {
    Cookies.remove("email", { path: "/" });
    Cookies.remove("role", { path: "/" });
    Cookies.remove("ec_auth_token", { path: "/" });

    navigate("/login", { replace: true });
  };

  useEffect(() => {
    const fetchUserProfile = async () => {
      // 1. Use js-cookie to ensure consistent decoding
      const useremail = Cookies.get("email");

      if (!useremail) {
        navigate("/login");
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // Safely encode the email so special characters (like + or @) don't break the GET request
        const safeEmail = encodeURIComponent(useremail);

        const response = await fetch(
          `${API_BASE_URL}/api/profile/read_byEmail?email=${safeEmail}`,
          {
            credentials: "include",
          },
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          // If 403 (session expired), redirect to login; other errors show error UI
          if (response.status === 403) {
            navigate("/login");
            return;
          }
          throw new Error(
            errorData.message || `HTTP error! status: ${response.status}`,
          );
        }

        const data = await response.json();
        const profileData = data.data ?? data;

        // New user: profile row exists but is null, redirect to complete profile
        if (profileData === null) {
          navigate("/dashboard/profileEdit", { replace: true });
          return;
        }

        setUserData(profileData);
      } catch (err) {
        console.error("Error fetching user profile:", err);
        setError("Failed to load profile. Please try again.");
        // Don't auto-logout on fetch errors — let the user decide
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserProfile();
  }, [navigate]);

  if (isLoading)
    return (
      <div className="loading-container">
        <div className="loader"></div>
      </div>
    );

  if (error || !userData) {
    return (
      <div className="status-container">
        <div className="status-card">
          <div className="visual-indicator error-state">
            <AlertCircle size={32} strokeWidth={2} />
          </div>

          <div className="error_text-content">
            <h2 className="error_heading">Profile Not Found</h2>
            <p className="error_description">
              {error ||
                "We couldn't retrieve your account data. Please check your connection."}
            </p>
          </div>

          <button className="action-trigger" onClick={handleLogout}>
            <span className="label">Login Again</span>
            <RefreshCw size={16} className="icon-spin" />
          </button>
        </div>
      </div>
    );
  }

  const collegeFullName = COLLEGE_MAP[userData.college] || userData.college;

  // Helper to process list items (comma separated strings)
  const renderList = (dataString) => {
    if (!dataString) return <span className="text-muted">Not added yet</span>;
    return dataString.split(",").map((item, index) => (
      <span key={index} className="premium-tag">
        {item.trim()}
      </span>
    ));
  };

  return (
    <div className="profile-wrapper">
      <div className="profile-card-container">
        {/* --- LEFT SIDEBAR --- */}
        <div className="profile-sidebar">
          <div className="sidebar-pattern"></div> {/* Decorative Overlay */}
          <div className="profile-header">
            <div className="profile-img-container">
              <img
                src={
                  userData.profile_image
                    ? `${API_IMAGE_BASE_URL}/${userData.profile_image}`
                    : "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='150' height='150' viewBox='0 0 150 150'%3E%3Crect width='150' height='150' fill='%23e2e8f0'/%3E%3Ccircle cx='75' cy='60' r='28' fill='%23a0aec0'/%3E%3Cellipse cx='75' cy='120' rx='44' ry='30' fill='%23a0aec0'/%3E%3C/svg%3E"
                }
                alt="Profile"
                className="profile-img"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src =
                    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='150' height='150' viewBox='0 0 150 150'%3E%3Crect width='150' height='150' fill='%23e2e8f0'/%3E%3Ccircle cx='75' cy='60' r='28' fill='%23a0aec0'/%3E%3Cellipse cx='75' cy='120' rx='44' ry='30' fill='%23a0aec0'/%3E%3C/svg%3E";
                }}
              />
              <div className="status-badge-wrapper">
                {userData.FacilitatorApproved === 1 ||
                userData.FacilitatorApproved === true ? (
                  <div
                    className="status-badge verified"
                    title="Verified Member"
                  >
                    <FaCheckCircle />
                  </div>
                ) : (
                  <div
                    className="status-badge pending"
                    title="Verification Pending"
                  >
                    <FaTimesCircle />
                  </div>
                )}
              </div>
            </div>

            <h2 className="profile-name">
              {userData.full_name || "Unknown User"}
            </h2>
            <p className="profile-role">
              <FaGraduationCap className="icon-inline" />
              {userData.department ? userData.department : "Student"}
            </p>
            {userData.Mentors_Name && (
              <div className="profile-mentor-box">
                <span className="mentor-label">Guided by</span>
                <span className="mentor-value">{userData.Mentors_Name}</span>
              </div>
            )}
          </div>
          <div className="profile-socials">
            {userData.social_links && (
              <a
                href={`https://www.instagram.com/${userData.social_links}`}
                target="_blank"
                rel="noopener noreferrer"
                className="social-btn instagram"
              >
                <FaInstagram /> <span>Instagram</span>
              </a>
            )}

            {userData.email && (
              <a href={`mailto:${userData.email}`} className="social-btn email">
                <FaEnvelope /> <span>Email Me</span>
              </a>
            )}
          </div>
        </div>

        {/* --- RIGHT CONTENT --- */}
        <div className="profile-content">
          <div className="tabs-header">
            <button
              className={`tab-btn ${activeTab === "Timeline" ? "active" : ""}`}
              onClick={() => handleTabClick("Timeline")}
            >
              Timeline
            </button>
            <button
              className={`tab-btn ${activeTab === "About" ? "active" : ""}`}
              onClick={() => handleTabClick("About")}
            >
              About
            </button>
            <button
              className={`tab-btn ${activeTab === "Insights" ? "active" : ""}`}
              onClick={() => handleTabClick("Insights")}
            >
              Insights
            </button>
          </div>

          <div className="tab-content fade-in">
            {/* --- TAB 1: TIMELINE --- */}
            {activeTab === "Timeline" && (
              <div className="info-grid">
                <div className="profile_info-card">
                  <h3>
                    <FaPhoneAlt className="card-icon" /> Contact Information
                  </h3>
                  <div className="info-row">
                    <div className="info-item">
                      <span className="label">Phone</span>
                      <span className="value">
                        {userData.phone_number || "N/A"}
                      </span>
                    </div>
                    <div className="info-item">
                      <span className="label">Email</span>
                      <span className="value">{userData.email || "N/A"}</span>
                    </div>
                    <div className="info-item full">
                      <span className="label">Address</span>
                      <span className="value">{userData.address || "N/A"}</span>
                    </div>
                  </div>
                </div>

                <div className="profile_info-card">
                  <h3>
                    <FaGraduationCap className="card-icon" /> Academic Details
                  </h3>
                  <div className="info-row">
                    <div className="info-item full">
                      <span className="label">College</span>
                      <span className="value highlight">
                        {collegeFullName || "N/A"}
                      </span>
                    </div>
                    <div className="info-item">
                      <span className="label">Course</span>
                      <span className="value">
                        {userData.department || "N/A"}
                      </span>
                    </div>
                    <div className="info-item">
                      <span className="label">Graduation Year</span>
                      <span className="value">
                        {userData.year_of_study || "N/A"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="profile_info-card">
                  <h3>
                    <FaBirthdayCake className="card-icon" /> Personal Details
                  </h3>
                  <div className="info-row three-col">
                    <div className="info-item">
                      <span className="label">Birthday</span>
                      <span className="value">{userData.dob || "N/A"}</span>
                    </div>
                    <div className="info-item">
                      <span className="label">Gender</span>
                      <span className="value">{userData.gender || "N/A"}</span>
                    </div>
                    <div className="info-item">
                      <span className="label">Blood Group</span>
                      <span className="value">
                        {userData.blood_group || "N/A"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* --- TAB 2: ABOUT --- */}
            {activeTab === "About" && (
              <div className="info-grid">
                <div className="profile_info-card full-height">
                  <h3>Biography</h3>
                  <div className="bio-text">
                    {userData.bio ? (
                      userData.bio
                        .split("\n")
                        .map((para, i) => <p key={i}>{para}</p>)
                    ) : (
                      <p className="text-muted">
                        No bio available. Add a bio to let people know about
                        you.
                      </p>
                    )}
                  </div>
                </div>

                <div className="profile_info-card">
                  <h3>Skills</h3>
                  <div className="tags-container">
                    {renderList(userData.skills)}
                  </div>
                </div>
              </div>
            )}

            {/* --- TAB 3: INSIGHTS (NEW) --- */}
            {activeTab === "Insights" && (
              <div className="info-grid">
                {/* Achievements Section */}
                <div className="profile_info-card gold-border">
                  <h3>
                    <FaTrophy className="card-icon gold" /> Achievements
                  </h3>
                  <div className="achievements-list">
                    {/* Assuming achievements is a string or array. Adjust logic based on real data structure */}
                    {userData.achievements ? (
                      <div className="achievement-item">
                        <FaCheckCircle className="check-icon" />
                        <p>{userData.achievements}</p>
                      </div>
                    ) : (
                      <p className="text-muted">No achievements listed yet.</p>
                    )}
                  </div>
                </div>

                {/* Inspiration Section */}
                <div className="profile_info-card quote-bg">
                  <h3>
                    <FaLightbulb className="card-icon" /> Inspiration
                  </h3>
                  <div className="inspiration-box">
                    <FaQuoteLeft className="quote-mark" />
                    <p className="inspiration-text">
                      {userData.inspiration_source ||
                        "Wisdom begins in wonder."}
                    </p>
                  </div>
                </div>

                {/* Split Row for Strength/Weakness/Hobbies */}
                <div className="split-grid">
                  <div className="profile_info-card">
                    <h3>
                      <FaDumbbell className="card-icon" /> Strengths
                    </h3>
                    <div className="tags-container">
                      {renderList(userData.strength)}
                    </div>
                  </div>

                  <div className="profile_info-card">
                    <h3>
                      <FaExclamationTriangle className="card-icon" /> Weaknesses
                    </h3>
                    <div className="tags-container">
                      {renderList(userData.weakness)}
                    </div>
                  </div>
                </div>

                <div className="profile_info-card">
                  <h3>
                    <FaPalette className="card-icon" /> Hobbies & Interests
                  </h3>
                  <div className="tags-container hobbies">
                    {renderList(userData.hobbies)}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileCard;
